/**
 * DashboardPeriodPicker - The date-range + preset selector that
 * drives the Dashboard's "current period" filter.
 *
 * Surfaces three quick presets (This Month / Last 30 Days / Last
 * Quarter) plus a custom range with two native date inputs.
 * Selecting a preset clears the from/to inputs and lets the server
 * apply its calendar-month default; selecting Custom enables both
 * date inputs.
 *
 * Owned at the organism tier because it composes multiple controls
 * with their own state interactions; the underlying date inputs are
 * native (no library) since custom date pickers don't earn their
 * weight here.
 *
 * Used by SuperAdminDashboard and ManagerDashboard. The TechDashboard
 * doesn't expose a period picker (its lists are short, scoped, and
 * don't need a comparison window).
 */
import React, { useState } from 'react';
import type { DashboardFilters } from '../../../services/dashboardService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Preset values map to (from, to) pairs the picker computes locally.
 * 'custom' switches the picker to use the explicit from/to inputs.
 */
export type DashboardPeriodPreset = 'this_month' | 'last_30_days' | 'last_quarter' | 'custom';

export interface DashboardPeriodPickerProps {
    /** Current filter values (controlled). The picker reads `from`/`to`. */
    value: DashboardFilters;
    /** Called with a new filter object on every preset/range change. */
    onChange: (next: DashboardFilters) => void;
    /** Optional extra classes for the outer wrapper */
    className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** Format a JS Date as ISO YYYY-MM-DD without timezone shenanigans. */
function toIsoDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Compute the (from, to) pair for a given preset using the local clock. */
function computePresetRange(preset: DashboardPeriodPreset): { from: string; to: string } | null {
    if (preset === 'custom') return null;
    const today = new Date();
    if (preset === 'this_month') {
        const from = new Date(today.getFullYear(), today.getMonth(), 1);
        const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { from: toIsoDate(from), to: toIsoDate(to) };
    }
    if (preset === 'last_30_days') {
        const to = new Date(today);
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        return { from: toIsoDate(from), to: toIsoDate(to) };
    }
    /* last_quarter — most recent completed calendar quarter */
    const month = today.getMonth();
    const currentQuarter = Math.floor(month / 3);
    const lastQuarter = (currentQuarter + 3) % 4;
    const yearOffset = currentQuarter === 0 ? -1 : 0;
    const startMonth = lastQuarter * 3;
    const from = new Date(today.getFullYear() + yearOffset, startMonth, 1);
    const to = new Date(today.getFullYear() + yearOffset, startMonth + 3, 0);
    return { from: toIsoDate(from), to: toIsoDate(to) };
}

/**
 * Reverse-lookup the active preset based on the current filter
 * values. If neither bound is set we treat that as "this_month"
 * (the server's default). If both bounds are set but don't match a
 * preset, we report 'custom'.
 */
function detectPreset(value: DashboardFilters): DashboardPeriodPreset {
    if (!value.from && !value.to) return 'this_month';
    const presets: DashboardPeriodPreset[] = ['this_month', 'last_30_days', 'last_quarter'];
    for (const p of presets) {
        const r = computePresetRange(p);
        if (r && r.from === value.from && r.to === value.to) return p;
    }
    return 'custom';
}

/* ============================================
 * COMPONENT
 * ============================================ */

const PRESET_LABEL: Record<DashboardPeriodPreset, string> = {
    this_month: 'This Month',
    last_30_days: 'Last 30 Days',
    last_quarter: 'Last Quarter',
    custom: 'Custom',
};

export const DashboardPeriodPicker: React.FC<DashboardPeriodPickerProps> = ({
    value,
    onChange,
    className,
}) => {
    // Track "custom" mode explicitly. detectPreset() can never return 'custom'
    // while the range still matches a preset, so inferring isCustom purely from
    // the value made the Custom button a no-op (it never revealed the date
    // inputs). customMode latches on when the user clicks Custom and clears when
    // they pick a quick preset; a value that genuinely matches no preset also
    // counts as custom.
    const detected = detectPreset(value);
    const [customMode, setCustomMode] = useState(false);
    const isCustom = customMode || detected === 'custom';
    const activePreset: DashboardPeriodPreset = isCustom ? 'custom' : detected;

    const choosePreset = (preset: DashboardPeriodPreset) => {
        if (preset === 'custom') {
            // Switch to custom — keep current from/to (or empty defaults
            // if none yet) so the user has something to edit.
            setCustomMode(true);
            onChange({ ...value, from: value.from || '', to: value.to || '' });
            return;
        }
        setCustomMode(false);
        const r = computePresetRange(preset);
        onChange({ ...value, from: r?.from, to: r?.to });
    };

    return (
        <div className={['dashboard-period-picker', 'flex flex-wrap items-end gap-12px', className ?? ''].join(' ')} data-ref="organism-dashboard-period-picker">
            {/* Preset buttons row */}
            <div className="dashboard-period-picker__presets flex flex-wrap gap-4px">
                {(Object.keys(PRESET_LABEL) as DashboardPeriodPreset[]).map((p) => {
                    const active = activePreset === p;
                    return (
                        <button
                            key={p}
                            type="button"
                            onClick={() => choosePreset(p)}
                            className={[
                                'dashboard-period-picker__item',
                                active ? 'dashboard-period-picker__item--active' : '',
                                'px-12px py-8px text-13px font-sans font-medium border rounded-md transition-colors min-h-44px desktop:min-h-36px',
                                active
                                    ? 'bg-brand-primary text-white border-brand-primary'
                                    : 'bg-white text-brand-text border-brand-border hover:border-brand-primary/40',
                            ].join(' ')}
                        >
                            {PRESET_LABEL[p]}
                        </button>
                    );
                })}
            </div>

            {/* Custom-range inputs — only enabled when Custom preset is active. */}
            {isCustom && (
                <div className="flex items-end gap-8px">
                    <div>
                        <label className="block text-11px font-sans uppercase tracking-wide text-brand-text-muted mb-2px">From</label>
                        <input
                            type="date"
                            value={value.from ?? ''}
                            onChange={(e) => onChange({ ...value, from: e.target.value })}
                            className="px-12px py-6px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white min-h-44px desktop:min-h-36px"
                        />
                    </div>
                    <div>
                        <label className="block text-11px font-sans uppercase tracking-wide text-brand-text-muted mb-2px">To</label>
                        <input
                            type="date"
                            value={value.to ?? ''}
                            onChange={(e) => onChange({ ...value, to: e.target.value })}
                            min={value.from || undefined}
                            className="px-12px py-6px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white min-h-44px desktop:min-h-36px"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardPeriodPicker;
